import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Stack,
  Divider,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { toast } from "react-toastify";
import { getUserDetails, updateUser, deleteUser } from "../../services/userService";
// import { useAuth } from "../../auth/AuthContext";

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await getUserDetails();
        if (res.success) {
          setUser(res.message);
          setForm(res.message);
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
        toast.error("Failed to load profile.");
      }
    };
    fetchUser();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const res = await updateUser({ name: form.name }); // update only name
      if (res.success) {
        setUser({ ...user, name: form.name });
        setEditMode(false);
        toast.success("Profile updated successfully.");
      } else {
        toast.error(res.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      toast.error("Something went wrong.");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await deleteUser();
      if (res.success) {
        toast.success("Account deleted successfully.");
        localStorage.removeItem("user");
        window.location.href = "/signin";
      } else {
        toast.error(res.message || "Failed to delete account.");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error("Something went wrong.");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.oldPassword || !passwordForm.newPassword) {
      toast.error("Please fill both old and new passwords.");
      return;
    }
    try {
      const res = await updateUser(passwordForm);
      if (res.success) {
        toast.success("Password updated successfully.");
        setPasswordDialogOpen(false);
        setPasswordForm({ oldPassword: "", newPassword: "" });
      } else {
        toast.error(res.message || "Failed to update password.");
      }
    } catch (err) {
      console.error("Error updating password:", err);
      toast.error("Something went wrong.");
    }
  };

  if (!user) {
    return (
      <Box p={5} textAlign="center">
        <Typography variant="h6">Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Box p={5}>
      {/* Header */}
      <Stack direction="row" spacing={3} alignItems="center" mb={4}>
        <Avatar
          sx={{
            width: 120,
            height: 120,
            fontSize: 48,
            bgcolor: "#1976d2",
          }}
        >
          {user.name?.charAt(0)}
        </Avatar>
        <Box>
          <Typography variant="h3" fontWeight="bold">
            {user.name}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {user.role}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
      </Stack>

      {/* Profile Info Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }} elevation={3}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Profile Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3} maxWidth={500}>
          <TextField label="User ID" value={form.id || ""} fullWidth disabled />
          <TextField
            label="Name"
            name="name"
            value={form.name || ""}
            onChange={handleChange}
            fullWidth
            disabled={!editMode}
          />
          <TextField label="Email" value={form.email || ""} fullWidth disabled />
          <TextField label="Role" value={form.role || ""} fullWidth disabled />
        </Stack>

        <Stack direction="row" spacing={2} mt={4}>
          {!editMode ? (
            <Button variant="contained" size="large" onClick={() => setEditMode(true)}>
              Edit Name
            </Button>
          ) : (
            <>
              <Button variant="contained" size="large" onClick={handleSave}>
                Save Changes
              </Button>
              <Button variant="outlined" size="large" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* Security Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }} elevation={3}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Security
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            size="large"
            color="warning"
            onClick={() => setPasswordDialogOpen(true)}
          >
            Change Password
          </Button>
          <Button
            variant="contained"
            size="large"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Account
          </Button>
        </Stack>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Old Password"
              type="password"
              value={passwordForm.oldPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePasswordChange}>
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;